"use client";

import { useEffect, useRef, useState } from "react";
import { createClient } from "@/lib/supabase/client";
import { useStore } from "@/lib/store";
import { cn } from "@/lib/utils";
import { VoiceRecorder } from "@/components/ui/VoiceRecorder";

type SlashCommand = {
  id: string;
  label: string;
  hint: string;
  insert: string;
  moveCursor?: number;
};

const COMMANDS: SlashCommand[] = [
  { id: "h1", label: "Heading 1", hint: "# Large title", insert: "# " },
  { id: "h2", label: "Heading 2", hint: "## Section", insert: "## " },
  { id: "h3", label: "Heading 3", hint: "### Subsection", insert: "### " },
  { id: "ul", label: "Bulleted list", hint: "- item", insert: "- " },
  { id: "ol", label: "Numbered list", hint: "1. item", insert: "1. " },
  { id: "todo", label: "To-do", hint: "- [ ] task", insert: "- [ ] " },
  { id: "quote", label: "Quote", hint: "> note", insert: "> " },
  { id: "code", label: "Code block", hint: "```lang", insert: "```\n\n```", moveCursor: 4 },
  { id: "divider", label: "Divider", hint: "---", insert: "\n---\n" },
  { id: "table", label: "Table", hint: "markdown table", insert: "| col | col |\n| --- | --- |\n| row | row |\n" },
  { id: "link", label: "Link", hint: "[text](url)", insert: "[text](url)", moveCursor: 1 },
  { id: "image", label: "Image (upload)", hint: "Supabase Storage", insert: "__IMAGE__" },
  { id: "file", label: "File attachment (upload)", hint: "PDF / CSV / ZIP / any", insert: "__FILE__" },
  { id: "video", label: "Video embed", hint: "YouTube / Loom / Vimeo URL", insert: "[video](https://)", moveCursor: 1 },
  { id: "wiki", label: "Wikilink to doc", hint: "[[Title]]", insert: "[[]]", moveCursor: 2 },
  { id: "issue", label: "Mention task", hint: "@RACI-12", insert: "@", moveCursor: 1 },
  { id: "tag", label: "Tag", hint: "#tag", insert: "#", moveCursor: 1 },
];

/**
 * Textarea wrapper that adds:
 * - Paste/drop image OR any file → upload to Supabase Storage
 *   `attachments` bucket (keyed by workspace id). Images insert as
 *   `![alt](url)`; other files insert as `[📎 name (size)](url)`.
 * - Slash menu (`/` at start of line or after whitespace): formatting,
 *   image upload, file upload, video embed (YouTube/Loom/Vimeo).
 * - Voice recorder button (PAUT-68) toggles MediaRecorder, uploads as
 *   audio/webm, inserts `[🎙️ voice note](url)`.
 *
 * PAUT-66: file attachments + video embed.
 */
export function MarkdownEditor({
  value,
  onChange,
  onBlur,
  placeholder,
  className,
  autoFocus,
}: {
  value: string;
  onChange: (v: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
}) {
  const activeWorkspaceId = useStore((s) => s.activeWorkspaceId);
  const ref = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const anyFileRef = useRef<HTMLInputElement>(null);
  const [slash, setSlash] = useState<{ start: number; query: string } | null>(null);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [uploading, setUploading] = useState(false);

  // Keep selection-change-based slash detection
  function handleChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    const v = e.target.value;
    onChange(v);
    detectSlash(v, e.target.selectionStart ?? v.length);
  }
  function detectSlash(text: string, caret: number) {
    // Walk back from caret to find `/` that starts a command
    let i = caret - 1;
    while (i >= 0) {
      const c = text[i];
      if (c === "/") {
        const before = i === 0 ? "" : text[i - 1];
        if (i === 0 || before === "\n" || before === " ") {
          setSlash({ start: i, query: text.slice(i + 1, caret) });
          setSelectedIdx(0);
          return;
        }
        break;
      }
      if (c === "\n" || c === " ") break;
      i--;
    }
    setSlash(null);
  }

  const filtered = slash
    ? COMMANDS.filter(
        (c) =>
          c.label.toLowerCase().includes(slash.query.toLowerCase()) ||
          c.id.startsWith(slash.query.toLowerCase())
      )
    : [];

  function applyCommand(cmd: SlashCommand) {
    if (!ref.current || !slash) return;
    if (cmd.id === "image") {
      setSlash(null);
      // Remove the slash query from text then trigger file picker
      const before = value.slice(0, slash.start);
      const after = value.slice(slash.start + 1 + slash.query.length);
      onChange(before + after);
      setTimeout(() => fileRef.current?.click(), 0);
      return;
    }
    if (cmd.id === "file") {
      setSlash(null);
      const before = value.slice(0, slash.start);
      const after = value.slice(slash.start + 1 + slash.query.length);
      onChange(before + after);
      setTimeout(() => anyFileRef.current?.click(), 0);
      return;
    }
    const before = value.slice(0, slash.start);
    const after = value.slice(slash.start + 1 + slash.query.length);
    const newText = before + cmd.insert + after;
    onChange(newText);
    const newCaret = before.length + cmd.insert.length - (cmd.moveCursor ?? 0);
    setSlash(null);
    requestAnimationFrame(() => {
      ref.current?.focus();
      ref.current?.setSelectionRange(newCaret, newCaret);
    });
  }

  async function uploadFile(file: File) {
    // PAUT-66: relax to any MIME. Image-vs-other rendering decided at insert
    // time by `insertAttachmentAtCursor` based on file.type.
    if (!activeWorkspaceId) return null;
    setUploading(true);
    try {
      const sb = createClient();
      const ext = file.name.split(".").pop() || "bin";
      const path = `${activeWorkspaceId}/${crypto.randomUUID()}.${ext}`;
      const { error } = await sb.storage.from("attachments").upload(path, file, {
        cacheControl: "3600",
        upsert: false,
        contentType: file.type || "application/octet-stream",
      });
      if (error) {
        console.error("[attachments] upload failed", error);
        return null;
      }
      const { data: pub } = sb.storage.from("attachments").getPublicUrl(path);
      return pub.publicUrl;
    } finally {
      setUploading(false);
    }
  }

  function formatBytes(n: number): string {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
    return `${(n / 1024 / 1024 / 1024).toFixed(1)} GB`;
  }

  async function insertAttachmentAtCursor(url: string, file: File) {
    const isImage = file.type.startsWith("image/");
    const isAudio = file.type.startsWith("audio/");
    const sizeStr = formatBytes(file.size);
    let snippet: string;
    if (isImage) {
      snippet = `\n![${file.name}](${url})\n`;
    } else if (isAudio) {
      snippet = `\n[🎙️ ${file.name} (${sizeStr})](${url})\n`;
    } else {
      // Generic file → markdown link prefixed with 📎. The Markdown renderer
      // (Markdown.tsx) detects the 📎 prefix and renders as a download chip.
      snippet = `\n[📎 ${file.name} (${sizeStr})](${url})\n`;
    }
    const ta = ref.current;
    if (!ta) {
      onChange(value + snippet);
      return;
    }
    const caret = ta.selectionStart ?? value.length;
    const newText = value.slice(0, caret) + snippet + value.slice(caret);
    onChange(newText);
    requestAnimationFrame(() => {
      ta.focus();
      const pos = caret + snippet.length;
      ta.setSelectionRange(pos, pos);
    });
  }

  async function handlePaste(e: React.ClipboardEvent<HTMLTextAreaElement>) {
    const items = Array.from(e.clipboardData?.items ?? []);
    const fileItem = items.find((it) => it.kind === "file");
    if (!fileItem) return;
    const file = fileItem.getAsFile();
    if (!file) return;
    e.preventDefault();
    const url = await uploadFile(file);
    if (url) void insertAttachmentAtCursor(url, file);
  }

  async function handleDrop(e: React.DragEvent<HTMLTextAreaElement>) {
    const files = Array.from(e.dataTransfer?.files ?? []);
    if (files.length === 0) return;
    e.preventDefault();
    for (const f of files) {
      const url = await uploadFile(f);
      if (url) await insertAttachmentAtCursor(url, f);
    }
  }

  async function handleFilePicker(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = await uploadFile(file);
    if (url) await insertAttachmentAtCursor(url, file);
    e.target.value = "";
  }

  function handleKey(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (slash && filtered.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIdx((i) => (i + 1) % filtered.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIdx((i) => (i - 1 + filtered.length) % filtered.length);
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        applyCommand(filtered[selectedIdx]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setSlash(null);
        return;
      }
    }
  }

  useEffect(() => {
    if (autoFocus) ref.current?.focus();
  }, [autoFocus]);

  return (
    <div className={cn("relative", className)}>
      <textarea
        ref={ref}
        value={value}
        onChange={handleChange}
        onPaste={handlePaste}
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        onBlur={onBlur}
        onKeyDown={handleKey}
        placeholder={placeholder}
        spellCheck={false}
        className="w-full h-full p-6 bg-transparent outline-none resize-none text-[13px] font-mono text-[var(--fg-secondary)] leading-relaxed scrollbar-thin"
        onSelect={(e) => {
          const ta = e.target as HTMLTextAreaElement;
          detectSlash(value, ta.selectionStart ?? value.length);
        }}
      />
      {uploading && (
        <div className="absolute bottom-3 right-3 px-2 py-1 rounded bg-[var(--bg-tertiary)] border border-[var(--border)] text-[11px] text-[var(--fg-tertiary)]">
          Uploading…
        </div>
      )}
      <div className="absolute top-3 right-3">
        <VoiceRecorder
          onInserted={(snippet) => {
            const ta = ref.current;
            if (!ta) {
              onChange(value + snippet);
              return;
            }
            const caret = ta.selectionStart ?? value.length;
            const next = value.slice(0, caret) + snippet + value.slice(caret);
            onChange(next);
            requestAnimationFrame(() => {
              ta.focus();
              const pos = caret + snippet.length;
              ta.setSelectionRange(pos, pos);
            });
          }}
        />
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        hidden
        onChange={handleFilePicker}
      />
      <input
        ref={anyFileRef}
        type="file"
        hidden
        onChange={handleFilePicker}
      />
      {slash && filtered.length > 0 && (
        <SlashMenu commands={filtered} selectedIdx={selectedIdx} onPick={applyCommand} />
      )}
    </div>
  );
}

function SlashMenu({
  commands,
  selectedIdx,
  onPick,
}: {
  commands: SlashCommand[];
  selectedIdx: number;
  onPick: (c: SlashCommand) => void;
}) {
  return (
    <div className="absolute left-6 bottom-6 w-[280px] rounded-md border border-[var(--border-strong)] bg-[var(--surface-elevated)] shadow-lg overflow-hidden text-[12px]">
      <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-[var(--fg-muted)]">
        Insert
      </div>
      <div className="max-h-[280px] overflow-y-auto scrollbar-thin">
        {commands.map((c, i) => (
          <button
            key={c.id}
            onMouseDown={(e) => {
              e.preventDefault();
              onPick(c);
            }}
            className={cn(
              "w-full flex items-center justify-between px-2 py-1.5 text-left",
              i === selectedIdx
                ? "bg-[var(--bg-active)] text-[var(--fg)]"
                : "hover:bg-[var(--bg-hover)] text-[var(--fg-secondary)]"
            )}
          >
            <span>{c.label}</span>
            <span className="text-[10.5px] text-[var(--fg-muted)] font-mono">{c.hint}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
